import { CustomLayoutDrawerContent } from 'module/ui/components/atoms/drawer/custom-layout-drawer';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { faRedo } from '@fortawesome/pro-solid-svg-icons';
import { Button, Drawer, DrawerTitle } from '@rocketlane/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Email } from 'api/email';
import { EmailPageTemplateUiState } from 'api/email-page-template/types';
import { PageType } from 'api/page-template/types';
import { useSelectedEmailId } from 'queries/emails';
import { useGetPageTemplatePreviewQuery } from 'queries/templates';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FlexWrapper, FlexWrapperWithGutter } from 'components/common/styles';
import { resolvePageTriggerListFilter } from 'components/organisms/pages-template/shared/page-trigger-list-filter';
import {
  DropdownSelector,
  ErrorAlert,
  PreviewContainer,
  PreviewHeader,
} from './components';
import { EmailList } from './email-list-dropdown';
import { usePreviewStreaming } from './hooks/use-preview-streaming';

const CenterWrapper = styled.div`
  width: calc(700px + calc(var(--spacing-06) * 2));
  padding: var(--spacing-05) var(--spacing-06) 0;
  margin: 0px auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
`;

function EmailPageLevelPreview({
  isPreviewOpen,
  setIsPreviewOpen,
  templateData,
}: {
  isPreviewOpen: boolean;
  setIsPreviewOpen: (isPreviewOpen: boolean) => void;
  templateData: EmailPageTemplateUiState;
}) {
  const { selectedEmailId, selectedEmailSubject, setSelectedEmailId } =
    useSelectedEmailId();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const queryClient = useQueryClient();

  const {
    data: templatePreview,
    isLoading: isPageLevelPreviewLoading,
    isFetching: isPageLevelPreviewFetching,
    refetch: refetchTemplatePreview,
    error: pagePreviewError,
  } = useGetPageTemplatePreviewQuery({
    meetingId: selectedEmailId ?? 0,
    pageType: PageType.Email,
    enabled: false,
    payload: 'tiptapJson' in templateData ? templateData.tiptapJson : undefined,
  });

  const {
    isInitializingStream,
    streamedContent,
    onInitialization,
    thinkingLogs,
  } = usePreviewStreaming({
    previewKey: selectedEmailId ?? 0,
    token: templatePreview?.streamingMeta?.token,
    channelName: templatePreview?.streamingMeta?.channelName,
    shouldFilterByPromptId: true,
    previewContent: templatePreview?.content,
  });

  useEffect(() => {
    if (selectedEmailId) {
      refetchTemplatePreview();
    }
  }, [selectedEmailId, refetchTemplatePreview]);

  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: ['page-template-preview'] });
    };
  }, [queryClient]);

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
  };

  const handleMenuClick = (emailId: number, email: Email) => {
    setSelectedEmailId(emailId, email.subject);
    setTimeout(() => {
      setIsDropdownOpen(false);
    }, 0);
  };

  const isLoading = isPageLevelPreviewLoading || isPageLevelPreviewFetching;
  const editorContent = streamedContent ?? templatePreview?.content;

  return (
    <Drawer
      open={isPreviewOpen}
      onClose={() => setIsPreviewOpen(false)}
      size="calc(100% - 60px)"
      placement="right"
      closable={false}
      showOverlay={false}
    >
      <DrawerTitle className="sr-only">Email page preview</DrawerTitle>
      <CustomLayoutDrawerContent>
        <CustomLayoutDrawerContent.Content>
          <FlexWrapperWithGutter>
            <PreviewHeader onClose={handleClosePreview} />
            <FlexWrapper
              style={{
                margin: 'auto',
                width: '690px',
                alignItems: 'center',
                gap: 'var(--spacing-03)',
              }}
            >
              <DropdownSelector
                isOpen={isDropdownOpen}
                onOpenChange={setIsDropdownOpen}
                selectedLabel={selectedEmailSubject}
                placeholder={<FormattedMessage id="preview.emails" />}
                dropdownContent={
                  <EmailList
                    height={400}
                    isDropdownOpen={isDropdownOpen}
                    isLoading={false}
                    selectedEmailId={selectedEmailId}
                    handleMenuClick={handleMenuClick}
                    filter={resolvePageTriggerListFilter(
                      PageType.Email,
                      templateData.filters.pageTriggerCondition,
                    )}
                  />
                }
              />
              <Button
                variant="icon"
                size="medium"
                type="button"
                leftIcon={<FontAwesomeIcon icon={faRedo} />}
                aria-label="Refresh preview"
                disabled={isLoading}
                onClick={() => refetchTemplatePreview()}
              />
            </FlexWrapper>
            <FlexWrapper>
              <Button
                variant="primary"
                type="button"
                onClick={() => setIsPreviewOpen(false)}
              >
                Close
              </Button>
            </FlexWrapper>
          </FlexWrapperWithGutter>
          <CenterWrapper>
            {templatePreview && (
              <PreviewContainer
                isLoading={isLoading}
                isInitializingStream={isInitializingStream}
                title={templatePreview.subject}
                content={editorContent}
                thinkingLogs={thinkingLogs}
                errorComponent={
                  pagePreviewError && (
                    <ErrorAlert message={pagePreviewError.message} />
                  )
                }
                noContentMessage={
                  <ErrorAlert message="No content available for preview" />
                }
                onInitialize={onInitialization}
              />
            )}
          </CenterWrapper>
        </CustomLayoutDrawerContent.Content>
      </CustomLayoutDrawerContent>
    </Drawer>
  );
}

export { EmailPageLevelPreview };
